import { getFormattedDataByExpiry } from '@benzinga/frontend-utils';
import type { EnhancedChainData, OrganizeOptionDataReturn } from '@benzinga/data-option-chain';

/**
 * True when at least one expiry has a usable chain — a strike row carrying both
 * a call and a put. Mirrors the `validExpiries` filter in `useOptionChain`, so
 * the page's noindex decision and the content's render gate stay in lockstep (a
 * raw `optionChainData.expiries` list can be non-empty while no expiry has
 * tradeable call+put data). `stockPrice` only sets an in-the-money flag in
 * `getFormattedDataByExpiry`, not chain existence, so it is irrelevant here.
 *
 * Shared by the option-chain `page.tsx` (generateMetadata robots), its
 * `content.tsx` (render gate), and `tab-data/tabs.ts` (nav tab visibility) so
 * all three agree on when the chain is worth showing — a nav tab never promises
 * a chain the page then can't render.
 */
export const hasValidOptionExpiry = (
  optionChainData: OrganizeOptionDataReturn | null | undefined,
  symbol: string,
): optionChainData is OrganizeOptionDataReturn => {
  const dataByExpiry = optionChainData?.dataByExpiry;
  const expiries = optionChainData?.expiries;
  if (!dataByExpiry || !Array.isArray(expiries) || expiries.length === 0) return false;
  const formatted = getFormattedDataByExpiry({ dataByExpiry, stockPrice: 0, symbol });
  return expiries.some(expiry => {
    const chains = (formatted?.[expiry.value] as EnhancedChainData | undefined)?.chains;
    return !!chains && chains.length > 0 && !!chains[0]?.call && !!chains[0]?.put;
  });
};
